import { useEffect, useState } from 'react'
import { krakenAPI } from '../../services/krakenApi'
import type { LiveRecord } from '../../types/kraken'
import { StatTile, WinRateTile, pnlClass } from '../trackRecord/recordUI'
import { duration, signedUsd, usd } from './tradeFormat'
import { formatPrice } from '../../utils/marketPrice'

/**
 * Live Kraken trades at ACTUAL fills — private to the admins who placed them,
 * never mixed into the signal engine's or Paper's record. Entries that never
 * filled are listed as not counted: no position, no win, no loss.
 */
const LiveRecordSection = () => {
  const [record, setRecord] = useState<LiveRecord | null>(null)
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    krakenAPI
      .record()
      .then(setRecord)
      .catch(() => setHidden(true))
  }, [])

  if (hidden || !record) return null

  return (
    <section className="space-y-4" aria-labelledby="live-record-h" data-testid="live-record">
      <div>
        <h2 id="live-record-h" className="text-xl font-bold text-gray-50">
          Live Kraken trades <span className="text-sm font-normal text-gray-400">· real money · admins only</span>
        </h2>
        <p className="text-xs text-gray-400">
          Every figure is Kraken's actual fill and fee, never a signal level. Kept separate from the records above.
          {record.unfilled_not_counted > 0 &&
            ` ${record.unfilled_not_counted} entr${record.unfilled_not_counted === 1 ? 'y' : 'ies'} never filled — not counted.`}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <WinRateTile
          winRate={record.win_rate}
          closed={record.closed}
          wins={record.wins}
          losses={record.losses}
          breakevens={record.breakevens}
          noun="closed"
        />
        <StatTile label="Net P&L" testId="live-net" value={signedUsd(record.net_pnl)} valueClass={pnlClass(record.net_pnl)}>
          gross {signedUsd(record.gross_pnl)}
        </StatTile>
        <StatTile label="Fees paid" testId="live-fees" value={usd(record.fees)} />
        <StatTile
          label="Return"
          testId="live-return"
          value={record.return_pct == null ? '—' : `${record.return_pct >= 0 ? '+' : ''}${record.return_pct.toFixed(2)}%`}
          valueClass={pnlClass(record.return_pct ?? 0)}
        >
          on capital deployed
        </StatTile>
      </div>
      {record.trades.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-terminal-border bg-terminal-surface">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                {['Closed', 'Symbol', 'Entry fill', 'Exit fill', 'Qty', 'Fees', 'Net', 'Exit', 'Held'].map((h) => (
                  <th key={h} className="px-3 py-2 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-terminal-border font-mono tabular-nums text-gray-200">
              {record.trades.map((t) => (
                <tr key={t.id}>
                  <td className="px-3 py-2 font-sans text-xs text-gray-400">{t.closed_at ? new Date(t.closed_at).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2 font-sans">{t.symbol}</td>
                  <td className="px-3 py-2">${t.avg_entry_price != null ? formatPrice(t.avg_entry_price) : '—'}</td>
                  <td className="px-3 py-2">${t.avg_exit_price != null ? formatPrice(t.avg_exit_price) : '—'}</td>
                  <td className="px-3 py-2">{t.exit_qty}</td>
                  <td className="px-3 py-2">{usd(t.fees_total)}</td>
                  <td className={`px-3 py-2 ${pnlClass(t.net_pnl ?? 0)}`}>{signedUsd(t.net_pnl ?? 0)}</td>
                  <td className="px-3 py-2 font-sans text-xs">{t.exit_reason ?? '—'}</td>
                  <td className="px-3 py-2 font-sans text-xs text-gray-400">{duration(t.opened_at, t.closed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export default LiveRecordSection
